import {
  Box,
  Center,
  Flex,
  Spinner,
  TabPanel,
  TabPanels,
  Tabs,
  useTheme,
} from '@chakra-ui/react';
import React, {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import {logChatMessageSend} from '../../utils/analytics';
import {isIphone} from '../../utils/deviceCheck';
import ChatHeader from './ChatHeader/LifestyleChatHeader';
import {CHAT_TABS} from './ChatHeader/ChatTabs';
import FeaturedTab from './FeaturedTab';
import RepliesTab from './RepliesMentionsTab';
import ThreadTab from './ThreadTab';
import {
  useAddCommentImage,
  useCreateLifestyleComment,
  useDeleteCommentImage,
  useDeleteLifestyleComment,
  useFetchRepliesAndMentions,
  useHelpfulLifestyleComment,
  useInfiniteLifestyleComments,
  useInfiniteFeaturedLifestyleComments,
  useReportLifestyleComment,
  useUpdateLifestyleComment,
  useFetchLifestyleChatUsers,
} from '../../hooks/useLifestyleComments';
import {getImage} from '../../pages/Profile/Edit/CropImage';
import {ChatMedia} from './ChatComment';

const fetchCommentsHook = useInfiniteLifestyleComments;

const LifestyleChat = () => {
  //   const {communityId, tab} = useParams();
  const {user} = useAuth();
  //   const fetchCommunity = useFetchCommunity(Number(communityId));
  //   const community = fetchCommunity.data?.data;
  const theme = useTheme();
  let [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = CHAT_TABS.indexOf(decodeURIComponent(searchParams.get('tab') || 'Thread'));
  const [headerHeight, setHeaderHeight] = useState(0);
	const [hideHeader, setHideHeader] = useState(false);
  const [tabIndex, setTabIndex] = useState(defaultTab == -1 ? 0 : defaultTab);

  //   const lastChatVisit = useLastChatVisit();

  const addLifestyleComment = useCreateLifestyleComment();
  const updateLifestyleComment = useUpdateLifestyleComment();
  const deleteLifestyleComment = useDeleteLifestyleComment();
  const reportLifestyleComment = useReportLifestyleComment();
  const helpfulLifestyleComment = useHelpfulLifestyleComment();
  const addCommentImage = useAddCommentImage();
  const deleteCommentImage = useDeleteCommentImage();

  const addComment = async (content: string, parent_id?: string, imageURLs?: Array<any>) => {
    if (content.length > 0) {
      const comment = await addLifestyleComment.mutateAsync({
        content,
        parent_id,
        user,
        hasImage: imageURLs && imageURLs.length > 0,
      });

      logChatMessageSend('lifestyle', 'lifestyle_plus');

      if (imageURLs && imageURLs.length > 0 && comment) {
        imageURLs.map(async (media) => {
          const croppedImage = await getImage(media, null);

          if (croppedImage) {
            await addCommentImage.mutateAsync({
              formData: croppedImage,
              comment_id: comment.data.id,
            });
          }
        });
      }
    }
  };

  const updateComment = async (
    commentId: string,
    content: string,
    parent_id?: string,
    imageURLs?: Array<any>,
    mediaToDelete?: ChatMedia[]
  ) => {
    if (content.length > 0) {
      await updateLifestyleComment.mutateAsync({
        commentId,
        content,
      });

      if (imageURLs && imageURLs.length > 0) {
        imageURLs.map(async (media) => {
          const croppedImage = await getImage(media, null);
          if (croppedImage && commentId) {
            await addCommentImage.mutateAsync({
              formData: croppedImage,
              comment_id: commentId,
            });
          }
        });
      }

      if (mediaToDelete && mediaToDelete.length > 0) {
        mediaToDelete.map(async (media) => {
          if (media.id) {
            await deleteCommentImage.mutateAsync({mediaId: media.id});
          }
        });
      }
    }
  };

  const deleteComment = async (commentId: string) => {
    await deleteLifestyleComment.mutateAsync({
      commentId,
    });
  };

  const reportComment = async (commentId: string) => {
    await reportLifestyleComment.mutateAsync({
      commentId,
    });
  };

  const helpfulComment = async (commentId: string) => {
    await helpfulLifestyleComment.mutateAsync({
      commentId,
    });
  };

  // to fix issue with overscroll on iOS
  useEffect(() => {
    if (isIphone) document.body.style.overflow = 'hidden';
    return () => {
      if (isIphone) document.body.style.overflow = 'unset';
    };
  }, []);

  //   useEffect(() => {
  //     let intervalId;
  //     if (community && community.id) {
  //       lastChatVisit.mutateAsync({communityId: community.id});
  //       intervalId = setInterval(() => {
  //         lastChatVisit.mutateAsync({communityId: community.id});
  //       }, 5000);
  //     }
  //     return () => {
  //       if (intervalId) clearInterval(intervalId);
  //     };
  //   }, [community]);

  useEffect(() => {
    setSearchParams({['tab']: CHAT_TABS[tabIndex]}, {replace: true});
  }, [tabIndex]);

	window.addEventListener('keyboardDidShow', () => {
		setHideHeader(true);
		setHeaderHeight(0);
});

window.addEventListener('keyboardDidHide', () => {
	setHideHeader(false);
});

  if (!user)
    return (
      <Center h="100vh">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor={theme.colors.lightGrey}
          color={theme.colors.lightBlue}
          size="lg"
        />
      </Center>
    );

  return (
    <Box>
      <Tabs
        outline={'none !important'}
        index={tabIndex}
        onChange={(index) => setTabIndex(index)}
        isLazy
        lazyBehavior="keepMounted">
        <Flex>
					{!hideHeader && (
            <ChatHeader
              addComment={addComment}
              updateComment={updateComment}
              deleteComment={deleteComment}
              reportComment={reportComment}
              helpfulComment={helpfulComment}
              setHeaderHeight={setHeaderHeight}
              tabIndex={tabIndex}
            />
					)}

          <TabPanels zIndex="auto" background="white">
            <TabPanel p={0}>
              <ThreadTab
                name={'Group Chat'}
                isRSVPd={true}
                showUserRSVP={false}
                fetchUsersHook={useFetchLifestyleChatUsers} //event ? useFetchChatUsers : useFetchCommunityChatUsers
                addComment={addComment}
                updateComment={updateComment}
                deleteComment={deleteComment}
                reportComment={reportComment}
                helpfulComment={helpfulComment}
                headerHeight={headerHeight}
                fetchCommentsHook={fetchCommentsHook}
              />
            </TabPanel>
            <TabPanel p={0}>
              <RepliesTab
                fetchRepliesHook={useFetchRepliesAndMentions}
                headerHeight={headerHeight}
              />
            </TabPanel>
            <TabPanel p={0}>
              <FeaturedTab
                name={'Group Chat'}
                isRSVPd={true}
                addComment={addComment}
                updateComment={updateComment}
                deleteComment={deleteComment}
                reportComment={reportComment}
                helpfulComment={helpfulComment}
                fetchFeaturedCommentsHook={useInfiniteFeaturedLifestyleComments}
                headerHeight={headerHeight}
              />
            </TabPanel>
          </TabPanels>
        </Flex>
      </Tabs>
    </Box>
  );
};

export default LifestyleChat;
